import { UiState } from '../state/UiState';
import { t } from '../i18n/i18n';

export interface UrlImportModalCallbacks {
  onLoadUrl: (url: string) => Promise<void>;
  onUpgradeRequested?: (feature: string) => void;
}

export class UrlImportModal {
  private element: HTMLElement;
  private uiState: UiState;
  private callbacks: UrlImportModalCallbacks;

  private titleEl!: HTMLElement;
  private descEl!: HTMLElement;
  private inputLabelEl!: HTMLElement;
  private inputUrlEl!: HTMLInputElement;
  private errorMsgEl!: HTMLElement;
  private samplesTitleEl!: HTMLElement;
  private sampleCatBtn!: HTMLButtonElement;
  private sampleSweetBtn!: HTMLButtonElement;
  private sampleSakuraBtn!: HTMLButtonElement;
  private noteEl!: HTMLElement;
  private btnSubmitEl!: HTMLButtonElement;
  private btnCloseEl!: HTMLButtonElement;
  private btnCancelEl!: HTMLButtonElement;
  private sampleLinksContainerEl!: HTMLElement;

  private isLoading = false;

  constructor(container: HTMLElement, uiState: UiState, callbacks: UrlImportModalCallbacks) {
    this.uiState = uiState;
    this.callbacks = callbacks;

    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.element.id = 'modalUrlImport';
    container.appendChild(this.element);

    this.render();
    this.initElements();
    this.bindEvents();

    this.uiState.subscribe(() => {
      this.updateTexts();
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  show(): void {
    const texts = t();
    if (!this.uiState.isUrlImportAllowed()) {
      this.callbacks.onUpgradeRequested?.(texts.upgrade.perkUrl);
      return;
    }
    this.resetState();
    this.updateTexts();
    this.element.classList.add('show');
    setTimeout(() => this.inputUrlEl?.focus(), 100);
  }

  hide(): void {
    if (this.isLoading) return;
    this.element.classList.remove('show');
    this.resetState();
  }

  private resetState(): void {
    if (this.inputUrlEl) this.inputUrlEl.value = '';
    if (this.errorMsgEl) {
      this.errorMsgEl.textContent = '';
      this.errorMsgEl.style.display = 'none';
    }
    this.setLoading(false);
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    const texts = t().urlModal;
    if (this.btnSubmitEl) {
      this.btnSubmitEl.disabled = loading;
      this.btnSubmitEl.innerHTML = loading ? texts.loading : texts.submit;
    }
    if (this.inputUrlEl) {
      this.inputUrlEl.disabled = loading;
    }
  }

  private showError(msg: string): void {
    if (this.errorMsgEl) {
      this.errorMsgEl.textContent = msg;
      this.errorMsgEl.style.display = 'block';
    }
  }

  private render(): void {
    const texts = t().urlModal;

    this.element.innerHTML = `
      <div class="modal-card" style="max-width: 500px;">
        <div class="modal-header">
          <div class="modal-title">
            <span>🌐</span>
            <span id="urlModalTitle">${texts.title}</span>
          </div>
          <button class="modal-close" id="btnCloseUrlImportModal">&times;</button>
        </div>
        <div class="modal-body">
          <p id="urlModalDesc" style="font-size: 13px; color: var(--color-text-main); margin-bottom: 12px; line-height: 1.5;">
            ${texts.desc}<br>
            <span style="font-size: 11px; color: var(--color-text-muted);">${texts.proNote}</span>
          </p>

          <div class="control-group">
            <label id="urlModalInputLabel" style="font-weight: 600; display: block; margin-bottom: 6px; font-size: 13px;">${texts.inputLabel}</label>
            <input
              type="url"
              id="inputImageUrl"
              placeholder="${texts.placeholder}"
              style="width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid var(--color-card-border); border-radius: var(--radius-sm); font-size: 13px; font-family: inherit; outline: none; transition: border-color 0.2s;"
            />
            <div id="urlImportError" style="display: none; color: #e53e3e; font-size: 12px; margin-top: 6px; line-height: 1.4;"></div>
          </div>

          <!-- クイックテスト用サンプルURL -->
          <div style="margin-top: 14px; padding: 10px 12px; background: #faf8f5; border-radius: var(--radius-sm); border: 1px dashed var(--color-card-border);">
            <div id="urlModalSamplesTitle" style="font-size: 11px; font-weight: 600; color: var(--color-text-muted); margin-bottom: 6px;">
              ${texts.samplesTitle}
            </div>
            <div id="sampleUrlLinks" style="display: flex; flex-wrap: wrap; gap: 6px;">
              <button type="button" class="btn btn-secondary btn-sample-url" id="sampleUrlCat" style="font-size: 11px; padding: 3px 8px;" data-url="https://images.unsplash.com/photo-1574158622682-e40e69881006?w=600&auto=format&fit=crop">
                ${texts.sampleCat}
              </button>
              <button type="button" class="btn btn-secondary btn-sample-url" id="sampleUrlSweet" style="font-size: 11px; padding: 3px 8px;" data-url="https://images.unsplash.com/photo-1563729784474-d77dbb933a9e?w=600&auto=format&fit=crop">
                ${texts.sampleSweet}
              </button>
              <button type="button" class="btn btn-secondary btn-sample-url" id="sampleUrlSakura" style="font-size: 11px; padding: 3px 8px;" data-url="https://images.unsplash.com/photo-1522383225653-ed111181a951?w=600&auto=format&fit=crop">
                ${texts.sampleSakura}
              </button>
            </div>
          </div>

          <!-- コピー＆ペースト(Ctrl+V)の親切な案内 -->
          <div id="urlModalTipPaste" style="margin-top: 12px; padding: 10px 12px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: var(--radius-sm); font-size: 11px; line-height: 1.5; color: #166534;">
            ${texts.tipPaste || '💡 DMM、X、Pixivなどの保護された画像は、画像を右クリックして「画像をコピー」し、画面上で Ctrl+V（貼り付け）を押すと一瞬で直接取り込めます！'}
          </div>

          <p id="urlModalNote" style="font-size: 11px; color: var(--color-text-muted); margin-top: 10px; line-height: 1.4;">
            ${texts.note}
          </p>
        </div>
        <div class="modal-footer" style="display: flex; justify-content: flex-end; gap: 8px;">
          <button class="btn btn-secondary" id="btnCancelUrlImport">${texts.cancel}</button>
          <button class="btn btn-primary" id="btnSubmitUrlImport">${texts.submit}</button>
        </div>
      </div>
    `;
  }

  private initElements(): void {
    this.titleEl = this.element.querySelector('#urlModalTitle') as HTMLElement;
    this.descEl = this.element.querySelector('#urlModalDesc') as HTMLElement;
    this.inputLabelEl = this.element.querySelector('#urlModalInputLabel') as HTMLElement;
    this.inputUrlEl = this.element.querySelector('#inputImageUrl') as HTMLInputElement;
    this.errorMsgEl = this.element.querySelector('#urlImportError') as HTMLElement;
    this.samplesTitleEl = this.element.querySelector('#urlModalSamplesTitle') as HTMLElement;
    this.sampleCatBtn = this.element.querySelector('#sampleUrlCat') as HTMLButtonElement;
    this.sampleSweetBtn = this.element.querySelector('#sampleUrlSweet') as HTMLButtonElement;
    this.sampleSakuraBtn = this.element.querySelector('#sampleUrlSakura') as HTMLButtonElement;
    this.noteEl = this.element.querySelector('#urlModalNote') as HTMLElement;
    this.btnSubmitEl = this.element.querySelector('#btnSubmitUrlImport') as HTMLButtonElement;
    this.btnCloseEl = this.element.querySelector('#btnCloseUrlImportModal') as HTMLButtonElement;
    this.btnCancelEl = this.element.querySelector('#btnCancelUrlImport') as HTMLButtonElement;
    this.sampleLinksContainerEl = this.element.querySelector('#sampleUrlLinks') as HTMLElement;
    this.tipPasteEl = this.element.querySelector('#urlModalTipPaste') as HTMLElement;
  }
  private tipPasteEl!: HTMLElement;

  private updateTexts(): void {
    const texts = t().urlModal;
    if (this.titleEl) this.titleEl.textContent = texts.title;
    if (this.descEl) {
      this.descEl.innerHTML = `
        ${texts.desc}<br>
        <span style="font-size: 11px; color: var(--color-text-muted);">${texts.proNote}</span>
      `;
    }
    if (this.inputLabelEl) this.inputLabelEl.textContent = texts.inputLabel;
    if (this.inputUrlEl) this.inputUrlEl.placeholder = texts.placeholder;
    if (this.samplesTitleEl) this.samplesTitleEl.textContent = texts.samplesTitle;
    if (this.sampleCatBtn) this.sampleCatBtn.textContent = texts.sampleCat;
    if (this.sampleSweetBtn) this.sampleSweetBtn.textContent = texts.sampleSweet;
    if (this.sampleSakuraBtn) this.sampleSakuraBtn.textContent = texts.sampleSakura;
    if (this.tipPasteEl) {
      this.tipPasteEl.textContent = texts.tipPaste || '💡 DMM、X、Pixivなどの保護された画像は、画像を右クリックして「画像をコピー」し、画面上で Ctrl+V（貼り付け）を押すと一瞬で直接取り込めます！';
    }
    if (this.noteEl) this.noteEl.textContent = texts.note;
    if (this.btnCancelEl) this.btnCancelEl.textContent = texts.cancel;
    if (this.btnSubmitEl && !this.isLoading) this.btnSubmitEl.innerHTML = texts.submit;
  }

  private bindEvents(): void {
    this.btnCloseEl?.addEventListener('click', () => this.hide());
    this.btnCancelEl?.addEventListener('click', () => this.hide());

    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        this.hide();
      }
    });

    // サンプルURLのクイック挿入
    this.sampleLinksContainerEl?.querySelectorAll<HTMLButtonElement>('.btn-sample-url').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.getAttribute('data-url');
        if (url && this.inputUrlEl) {
          this.inputUrlEl.value = url;
          this.errorMsgEl.style.display = 'none';
          this.inputUrlEl.focus();
        }
      });
    });

    // Enterキーで送信
    this.inputUrlEl?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit();
      }
    });

    this.btnSubmitEl?.addEventListener('click', () => {
      this.handleSubmit();
    });
  }

  private async handleSubmit(): Promise<void> {
    if (this.isLoading) return;
    const texts = t().urlModal;

    const rawUrl = this.inputUrlEl.value.trim();
    if (!rawUrl) {
      this.showError(texts.errEmpty);
      this.inputUrlEl.focus();
      return;
    }

    if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://') && !rawUrl.startsWith('data:image/')) {
      this.showError(texts.errInvalid);
      this.inputUrlEl.focus();
      return;
    }

    this.setLoading(true);
    this.errorMsgEl.style.display = 'none';

    try {
      await this.callbacks.onLoadUrl(rawUrl);
      this.setLoading(false);
      this.hide();
    } catch (err) {
      this.setLoading(false);
      const msg = (err as Error).message || texts.errFailed;
      this.showError(msg);
      this.inputUrlEl.focus();
    }
  }
}
